import React, { useEffect, useState } from 'react';
import { CircleLoader } from 'react-spinners';

import { useGlobalState } from '../../contexts/GlobalStateContext';

import { Button } from './button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from './sheet';
import { getSheetButtons, renderSheetContent } from './sheet-registry';

const GlobalSheetRenderer: React.FC = () => {
  const { state, hideSheet } = useGlobalState();
  const [buttonStates, setButtonStates] = useState<Record<string, any>>({});

  // Update button states periodically to handle dynamic changes
  useEffect(() => {
    const interval = setInterval(() => {
      const newButtonStates: Record<string, any> = {};

      state.sheets.forEach(sheet => {
        if (sheet.type === 'customer-create') {
          const handlers = (window as any).customerCreateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              currentStep: handlers.currentStep,
              totalSteps: handlers.totalSteps,
              isLoading: handlers.isLoading,
            };
          }
        } else if (sheet.type === 'customer-update') {
          const handlers = (window as any).customerUpdateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              currentStep: handlers.currentStep,
              totalSteps: handlers.totalSteps,
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'inventory-create' ||
          sheet.type === 'create-inventory' ||
          sheet.type === 'create-product'
        ) {
          const handlers = (window as any).inventoryCreateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              currentStep: handlers.currentStep,
              totalSteps: handlers.totalSteps,
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'inventory-update' ||
          sheet.type === 'edit-inventory' ||
          sheet.type === 'edit-product'
        ) {
          const handlers = (window as any).inventoryUpdateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              currentStep: handlers.currentStep,
              totalSteps: handlers.totalSteps,
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'order-create' ||
          sheet.type === 'create-order'
        ) {
          const handlers = (window as any).orderCreateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              currentStep: handlers.currentStep,
              totalSteps: handlers.totalSteps,
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'order-update' ||
          sheet.type === 'edit-order'
        ) {
          const handlers = (window as any).orderUpdateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'discount-create' ||
          sheet.type === 'create-discount'
        ) {
          const handlers = (window as any).discountCreateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              currentStep: handlers.currentStep,
              totalSteps: handlers.totalSteps,
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'discount-update' ||
          sheet.type === 'edit-discount'
        ) {
          const handlers = (window as any).discountUpdateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              currentStep: handlers.currentStep,
              totalSteps: handlers.totalSteps,
              isLoading: handlers.isLoading,
            };
          }
        } else if (sheet.type === 'bulk-permission-update') {
          const handlers = (window as any).bulkPermissionUpdateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              isLoading: handlers.isLoading,
              isDisabled: handlers.isDisabled,
              selectedUsersCount: handlers.selectedUsersCount,
            };
          }
        } else if (
          sheet.type === 'role-create' ||
          sheet.type === 'create-role'
        ) {
          const handlers = (window as any).roleCreateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              isLoading: handlers.isLoading,
            };
          }
        } else if (sheet.type === 'role-update') {
          const handlers = (window as any).roleUpdateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              isLoading: handlers.isLoading,
            };
          }
        } else if (sheet.type === 'send-notification') {
          const handlers = (window as any).sendNotificationHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'user-create' ||
          sheet.type === 'create-user'
        ) {
          const handlers = (window as any).userCreateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              isLoading: handlers.isLoading,
            };
          }
        } else if (sheet.type === 'user-update') {
          const handlers = (window as any).userUpdateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'rider-create' ||
          sheet.type === 'create-rider'
        ) {
          const handlers = (window as any).riderCreateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              currentStep: handlers.currentStep,
              totalSteps: handlers.totalSteps,
              isLoading: handlers.isLoading,
            };
          }
        } else if (
          sheet.type === 'rider-update' ||
          sheet.type === 'edit-rider'
        ) {
          const handlers = (window as any).riderUpdateHandlers;
          if (handlers) {
            newButtonStates[sheet.id] = {
              isLoading: handlers.isLoading,
            };
          }
        }
      });

      setButtonStates(newButtonStates);
    }, 100); // Update every 100ms

    return () => clearInterval(interval);
  }, [state.sheets]);

  const getDynamicButtonLabel = (sheetType: string, sheetId: string) => {
    if (sheetType === 'customer-create') {
      const handlers = (window as any).customerCreateHandlers;
      if (handlers && handlers.getButtonText) {
        return handlers.getButtonText();
      }
      // Fallback to old logic if handlers not available
      const buttonState = buttonStates[sheetId];
      if (buttonState) {
        const { currentStep, totalSteps } = buttonState;
        if (totalSteps === 1) {
          return 'Create Customer';
        }
        if (currentStep < totalSteps) {
          if (currentStep === 1) {
            return 'Proceed to KYC';
          } else if (currentStep === 2) {
            return 'Proceed to Payment Details';
          }
          return 'Next';
        } else {
          return 'Create Customer';
        }
      }
    } else if (sheetType === 'customer-update') {
      const handlers = (window as any).customerUpdateHandlers;
      if (handlers && handlers.getButtonText) {
        return handlers.getButtonText();
      }
      // Fallback to old logic if handlers not available
      const buttonState = buttonStates[sheetId];
      if (buttonState) {
        const { currentStep, totalSteps } = buttonState;

        // If only 1 step (B2C or B2B instant), show "Update Customer"
        if (totalSteps === 1) {
          return 'Update Customer';
        }

        // Multi-step flow (B2B credit)
        if (currentStep < totalSteps) {
          if (currentStep === 1) {
            return 'Proceed to KYC';
          } else if (currentStep === 2) {
            return 'Proceed to Payment Details';
          }
          return 'Next';
        } else {
          return 'Update Customer';
        }
      }
    } else if (
      sheetType === 'inventory-create' ||
      sheetType === 'create-inventory' ||
      sheetType === 'create-product'
    ) {
      const buttonState = buttonStates[sheetId];
      if (buttonState) {
        const { currentStep, totalSteps } = buttonState;

        if (currentStep < totalSteps) {
          if (currentStep === 1) {
            return 'Proceed to Variants';
          }
          return 'Next';
        } else {
          return 'Submit for Approval';
        }
      }
    } else if (
      sheetType === 'inventory-update' ||
      sheetType === 'edit-inventory' ||
      sheetType === 'edit-product'
    ) {
      const buttonState = buttonStates[sheetId];
      if (buttonState) {
        const { currentStep, totalSteps } = buttonState;

        // Single step for update
        if (totalSteps === 1) {
          return 'Update Product';
        }

        if (currentStep < totalSteps) {
          return 'Next';
        } else {
          return 'Update Product';
        }
      }
    } else if (
      sheetType === 'discount-create' ||
      sheetType === 'create-discount'
    ) {
      const buttonState = buttonStates[sheetId];
      if (buttonState) {
        const { currentStep, totalSteps } = buttonState;

        if (currentStep < totalSteps) {
          if (currentStep === 1) {
            return 'Proceed to Eligibility & Conditions';
          }
          return 'Next';
        } else {
          return 'Submit for Approval';
        }
      }
    } else if (
      sheetType === 'discount-update' ||
      sheetType === 'edit-discount'
    ) {
      const buttonState = buttonStates[sheetId];
      if (buttonState) {
        const { currentStep, totalSteps } = buttonState;

        if (currentStep < totalSteps) {
          if (currentStep === 1) {
            return 'Proceed to Eligibility & Conditions';
          } else if (currentStep === 2) {
            return 'Proceed to Status & Settings';
          }
          return 'Next';
        } else {
          return 'Update Discount';
        }
      }
    } else if (sheetType === 'order-create' || sheetType === 'create-order') {
      const buttonState = buttonStates[sheetId];
      if (buttonState) {
        const { currentStep, totalSteps } = buttonState;
        if (currentStep < totalSteps) {
          const stepLabels = {
            1: 'Add Products',
            2: 'Set Delivery',
            3: 'Review Order',
          };
          return stepLabels[currentStep as keyof typeof stepLabels] || 'Next';
        } else {
          return 'Create Order';
        }
      }
    } else if (sheetType === 'order-update' || sheetType === 'edit-order') {
      return 'Update Order';
    } else if (sheetType === 'rider-create' || sheetType === 'create-rider') {
      const buttonState = buttonStates[sheetId];
      if (buttonState) {
        const { currentStep, totalSteps } = buttonState;
        if (currentStep < totalSteps) {
          const stepLabels = {
            1: 'Set Vehicle Info',
            2: 'Set Service Areas',
          };
          return stepLabels[currentStep as keyof typeof stepLabels] || 'Next';
        } else {
          return 'Create Rider';
        }
      }
    } else if (sheetType === 'rider-update' || sheetType === 'edit-rider') {
      return 'Update Rider';
    }
    return 'Next';
  };

  const getDynamicButtonLoading = (sheetType: string, sheetId: string) => {
    if (sheetType === 'customer-create') {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (sheetType === 'customer-update') {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (
      sheetType === 'inventory-create' ||
      sheetType === 'create-inventory' ||
      sheetType === 'create-product'
    ) {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (
      sheetType === 'inventory-update' ||
      sheetType === 'edit-inventory' ||
      sheetType === 'edit-product'
    ) {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (
      sheetType === 'discount-create' ||
      sheetType === 'create-discount'
    ) {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (
      sheetType === 'discount-update' ||
      sheetType === 'edit-discount'
    ) {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (sheetType === 'order-create' || sheetType === 'create-order') {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (sheetType === 'order-update' || sheetType === 'edit-order') {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (sheetType === 'rider-create' || sheetType === 'create-rider') {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    } else if (sheetType === 'rider-update' || sheetType === 'edit-rider') {
      const buttonState = buttonStates[sheetId];
      return buttonState?.isLoading || false;
    }
    return false;
  };

  const getCustomerCreateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Back button (only show if not on first step)
    if (buttonState && buttonState.currentStep > 1) {
      buttons.push({
        id: 'back-customer',
        label: 'Back',
        onClick: () => {
          const handlers = (window as any).customerCreateHandlers;
          if (handlers) {
            handlers.handlePrevStep();
          }
        },
        color: 'gray' as const,
        hierarchy: 'secondary' as const,
        type: 'button' as const,
        icon: undefined,
      });
    }

    // Primary action button
    buttons.push({
      id: 'customer-action',
      label: getDynamicButtonLabel('customer-create', sheetId),
      onClick: () => {
        const handlers = (window as any).customerCreateHandlers;
        if (handlers) {
          handlers.handleNextStep();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('customer-create', sheetId),
      disabled: getDynamicButtonLoading('customer-create', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-customer',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).customerCreateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getCustomerUpdateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Back button (only show if not on first step)
    if (buttonState && buttonState.currentStep > 1) {
      buttons.push({
        id: 'back-customer-update',
        label: 'Back',
        onClick: () => {
          const handlers = (window as any).customerUpdateHandlers;
          if (handlers) {
            handlers.handlePrevStep();
          }
        },
        color: 'gray' as const,
        hierarchy: 'secondary' as const,
        type: 'button' as const,
        icon: undefined,
      });
    }

    // Primary action button
    buttons.push({
      id: 'customer-update-action',
      label: getDynamicButtonLabel('customer-update', sheetId),
      onClick: () => {
        const handlers = (window as any).customerUpdateHandlers;
        if (handlers) {
          handlers.handleNextStep();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('customer-update', sheetId),
      disabled: getDynamicButtonLoading('customer-update', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-customer-update',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).customerUpdateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getDiscountCreateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Back button (only show if not on first step)
    if (buttonState && buttonState.currentStep > 1) {
      buttons.push({
        id: 'back-discount',
        label: 'Previous',
        onClick: () => {
          const handlers = (window as any).discountCreateHandlers;
          if (handlers) {
            handlers.handlePrevStep();
          }
        },
        color: 'gray' as const,
        hierarchy: 'secondary' as const,
        type: 'button' as const,
        icon: undefined,
      });
    }

    // Primary action button (Next/Submit) — match customer-create order
    buttons.push({
      id: 'discount-action',
      label: getDynamicButtonLabel('discount-create', sheetId),
      onClick: () => {
        const handlers = (window as any).discountCreateHandlers;
        const btnState = buttonState;
        if (handlers) {
          try {
            if (
              btnState?.currentStep >= btnState?.totalSteps &&
              typeof handlers.handleSubmitAction === 'function'
            ) {
              handlers.handleSubmitAction();
            } else {
              handlers.handleNextStep();
            }
          } catch (err) {
            // Handle error silently
          }
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('discount-create', sheetId),
      disabled: getDynamicButtonLoading('discount-create', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button last
    buttons.push({
      id: 'cancel-discount',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).discountCreateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getDiscountUpdateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Back button (only show if not on first step)
    if (buttonState && buttonState.currentStep > 1) {
      buttons.push({
        id: 'back-discount-update',
        label: 'Previous',
        onClick: () => {
          const handlers = (window as any).discountUpdateHandlers;
          if (handlers) {
            handlers.handlePrevStep();
          }
        },
        color: 'gray' as const,
        hierarchy: 'outlined' as const,
        type: 'button' as const,
        icon: undefined,
      });
    }

    // Cancel button
    buttons.push({
      id: 'cancel-discount-update',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).discountUpdateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'outlined' as const,
      type: 'button' as const,
      icon: undefined,
    });

    // Primary action button
    buttons.push({
      id: 'discount-update-action',
      label: getDynamicButtonLabel('discount-update', sheetId),
      onClick: () => {
        const handlers = (window as any).discountUpdateHandlers;
        const btnState = buttonState;
        if (handlers) {
          try {
            if (
              btnState?.currentStep >= btnState?.totalSteps &&
              typeof handlers.handleSubmitAction === 'function'
            ) {
              handlers.handleSubmitAction();
            } else {
              handlers.handleNextStep();
            }
          } catch (err) {
            // Handle error silently
          }
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('discount-update', sheetId),
      disabled: getDynamicButtonLoading('discount-update', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getInventoryCreateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Back button (only show if not on first step)
    if (buttonState && buttonState.currentStep > 1) {
      buttons.push({
        id: 'back-inventory',
        label: 'Back to Product Details',
        onClick: () => {
          const handlers = (window as any).inventoryCreateHandlers;
          if (handlers) {
            handlers.handlePrevStep();
          }
        },
        color: 'gray' as const,
        hierarchy: 'outlined' as const,
        type: 'button' as const,
        icon: undefined,
      });
    }

    // Cancel button
    buttons.push({
      id: 'cancel-inventory',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).inventoryCreateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'outlined' as const,
      type: 'button' as const,
      icon: undefined,
    });

    // Primary action button
    buttons.push({
      id: 'inventory-action',
      label: getDynamicButtonLabel('inventory-create', sheetId),
      onClick: () => {
        const handlers = (window as any).inventoryCreateHandlers;
        if (handlers) {
          handlers.handleNextStep();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('inventory-create', sheetId),
      disabled: getDynamicButtonLoading('inventory-create', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getInventoryUpdateButtons = (sheetId: string) => {
    const handlers = (window as any).inventoryUpdateHandlers;
    const currentStep = handlers?.currentStep || 1;
    const buttons = [] as any[];

    // Back button (show on step > 1)
    if (currentStep > 1) {
      buttons.push({
        id: 'back-inventory-update',
        label: 'Back',
        onClick: () => {
          const h = (window as any).inventoryUpdateHandlers;
          if (h) h.handlePrevStep();
        },
        color: 'gray',
        hierarchy: 'secondary',
        type: 'button',
      });
    }

    // Cancel button
    buttons.push({
      id: 'cancel-inventory-update',
      label: 'Cancel',
      onClick: () => {
        const h = (window as any).inventoryUpdateHandlers;
        if (h) h.handleCancel();
      },
      color: 'gray',
      hierarchy: 'outlined',
      type: 'button',
    });

    // Primary action button
    buttons.push({
      id: 'inventory-update-action',
      label: getDynamicButtonLabel('inventory-update', sheetId),
      onClick: () => {
        const h = (window as any).inventoryUpdateHandlers;
        if (h) h.handleNextStep();
      },
      color: 'brand',
      hierarchy: 'primary',
      loading: getDynamicButtonLoading('inventory-update', sheetId),
      disabled: getDynamicButtonLoading('inventory-update', sheetId),
      type: 'button',
    });

    return buttons;
  };

  const getBulkPermissionUpdateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Primary action button
    const selectedUsersCount = buttonState?.selectedUsersCount || 0;
    buttons.push({
      id: 'bulk-update',
      label: `Update ${selectedUsersCount} User${selectedUsersCount !== 1 ? 's' : ''}`,
      onClick: () => {
        const handlers = (window as any).bulkPermissionUpdateHandlers;
        if (handlers) {
          handlers.handleBulkUpdate();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: buttonState?.isLoading || false,
      disabled: buttonState?.isDisabled || false,
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-bulk-update',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).bulkPermissionUpdateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getRoleCreateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Primary action button
    buttons.push({
      id: 'create-role',
      label: 'Create Role',
      onClick: () => {
        const handlers = (window as any).roleCreateHandlers;
        if (handlers) {
          handlers.handleCreateRole();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: buttonState?.isLoading || false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-role',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).roleCreateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      loading: false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getRoleUpdateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Primary action button
    buttons.push({
      id: 'update-role',
      label: 'Update Role',
      onClick: () => {
        const handlers = (window as any).roleUpdateHandlers;
        if (handlers) {
          handlers.handleUpdateRole();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: buttonState?.isLoading || false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    // Delete button
    buttons.push({
      id: 'delete-role',
      label: 'Delete Role',
      onClick: () => {
        const handlers = (window as any).roleUpdateHandlers;
        if (handlers) {
          handlers.showDeleteConfirmation();
        }
      },
      color: 'destructive' as const,
      hierarchy: 'secondary' as const,
      loading: false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-role-update',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).roleUpdateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      loading: false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getSendNotificationButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Primary action button
    buttons.push({
      id: 'send-notification',
      label: 'Send Notification',
      onClick: () => {
        const handlers = (window as any).sendNotificationHandlers;
        if (handlers) {
          handlers.handleSendNotification();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: buttonState?.isLoading || false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-notification',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).sendNotificationHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      loading: false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getUserCreateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Primary action button
    buttons.push({
      id: 'create-user',
      label: 'Create User',
      onClick: () => {
        const handlers = (window as any).userCreateHandlers;
        if (handlers) {
          handlers.handleCreateUser();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: buttonState?.isLoading || false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-user',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).userCreateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      loading: false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getUserUpdateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Primary action button
    buttons.push({
      id: 'update-user',
      label: 'Update User',
      onClick: () => {
        const handlers = (window as any).userUpdateHandlers;
        if (handlers) {
          handlers.handleUpdateUser();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: buttonState?.isLoading || false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-user-update',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).userUpdateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      loading: false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getOrderCreateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Back button (only show if not on first step)
    if (buttonState && buttonState.currentStep > 1) {
      buttons.push({
        id: 'back-order',
        label: 'Back',
        onClick: () => {
          const handlers = (window as any).orderCreateHandlers;
          if (handlers) {
            handlers.handlePrevStep();
          }
        },
        color: 'gray' as const,
        hierarchy: 'secondary' as const,
        type: 'button' as const,
        icon: undefined,
      });
    }

    // Primary action button
    buttons.push({
      id: 'order-action',
      label: getDynamicButtonLabel('order-create', sheetId),
      onClick: () => {
        const handlers = (window as any).orderCreateHandlers;
        if (handlers) {
          handlers.handleNextStep();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('order-create', sheetId),
      disabled: getDynamicButtonLoading('order-create', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-order',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).orderCreateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getOrderUpdateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Primary action button
    buttons.push({
      id: 'update-order',
      label: getDynamicButtonLabel('order-update', sheetId),
      onClick: () => {
        const handlers = (window as any).orderUpdateHandlers;
        if (handlers) {
          handlers.handleSubmit();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('order-update', sheetId),
      disabled: getDynamicButtonLoading('order-update', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-order-update',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).orderUpdateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      loading: false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getRiderCreateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Back button (only show if not on first step)
    if (buttonState && buttonState.currentStep > 1) {
      buttons.push({
        id: 'back-rider',
        label: 'Back',
        onClick: () => {
          const handlers = (window as any).riderCreateHandlers;
          if (handlers) {
            handlers.handlePrevStep();
          }
        },
        color: 'gray' as const,
        hierarchy: 'secondary' as const,
        type: 'button' as const,
        icon: undefined,
      });
    }

    // Primary action button
    buttons.push({
      id: 'rider-action',
      label: getDynamicButtonLabel('rider-create', sheetId),
      onClick: () => {
        const handlers = (window as any).riderCreateHandlers;
        if (handlers) {
          handlers.handleNextStep();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('rider-create', sheetId),
      disabled: getDynamicButtonLoading('rider-create', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-rider',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).riderCreateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  const getRiderUpdateButtons = (sheetId: string) => {
    const buttonState = buttonStates[sheetId];
    const buttons = [];

    // Primary action button
    buttons.push({
      id: 'update-rider',
      label: getDynamicButtonLabel('rider-update', sheetId),
      onClick: () => {
        const handlers = (window as any).riderUpdateHandlers;
        if (handlers) {
          handlers.handleSubmit();
        }
      },
      color: 'brand' as const,
      hierarchy: 'primary' as const,
      loading: getDynamicButtonLoading('rider-update', sheetId),
      disabled: getDynamicButtonLoading('rider-update', sheetId),
      type: 'button' as const,
      icon: undefined,
    });

    // Cancel button
    buttons.push({
      id: 'cancel-rider-update',
      label: 'Cancel',
      onClick: () => {
        const handlers = (window as any).riderUpdateHandlers;
        if (handlers) {
          handlers.handleCancel();
        }
      },
      color: 'gray' as const,
      hierarchy: 'secondary' as const,
      loading: false,
      disabled: buttonState?.isLoading || false,
      type: 'button' as const,
      icon: undefined,
    });

    return buttons;
  };

  return (
    <>
      {state.sheets.map(sheet => {
        // Get dynamic buttons for the sheet type
        let buttonsToRender;

        if (sheet.type === 'customer-create') {
          buttonsToRender = getCustomerCreateButtons(sheet.id);
        } else if (sheet.type === 'customer-update') {
          buttonsToRender = getCustomerUpdateButtons(sheet.id);
        } else if (
          sheet.type === 'inventory-create' ||
          sheet.type === 'create-inventory' ||
          sheet.type === 'create-product'
        ) {
          buttonsToRender = getInventoryCreateButtons(sheet.id);
        } else if (
          sheet.type === 'inventory-update' ||
          sheet.type === 'edit-inventory' ||
          sheet.type === 'edit-product'
        ) {
          buttonsToRender = getInventoryUpdateButtons(sheet.id);
        } else if (
          sheet.type === 'discount-create' ||
          sheet.type === 'create-discount'
        ) {
          buttonsToRender = getDiscountCreateButtons(sheet.id);
        } else if (
          sheet.type === 'discount-update' ||
          sheet.type === 'edit-discount'
        ) {
          buttonsToRender = getDiscountUpdateButtons(sheet.id);
        } else if (sheet.type === 'bulk-permission-update') {
          buttonsToRender = getBulkPermissionUpdateButtons(sheet.id);
        } else if (
          sheet.type === 'role-create' ||
          sheet.type === 'create-role'
        ) {
          buttonsToRender = getRoleCreateButtons(sheet.id);
        } else if (sheet.type === 'role-update') {
          buttonsToRender = getRoleUpdateButtons(sheet.id);
        } else if (sheet.type === 'send-notification') {
          buttonsToRender = getSendNotificationButtons(sheet.id);
        } else if (
          sheet.type === 'user-create' ||
          sheet.type === 'create-user'
        ) {
          buttonsToRender = getUserCreateButtons(sheet.id);
        } else if (sheet.type === 'user-update') {
          buttonsToRender = getUserUpdateButtons(sheet.id);
        } else if (
          sheet.type === 'order-create' ||
          sheet.type === 'create-order'
        ) {
          buttonsToRender = getOrderCreateButtons(sheet.id);
        } else if (
          sheet.type === 'order-update' ||
          sheet.type === 'edit-order'
        ) {
          buttonsToRender = getOrderUpdateButtons(sheet.id);
        } else if (
          sheet.type === 'rider-create' ||
          sheet.type === 'create-rider'
        ) {
          buttonsToRender = getRiderCreateButtons(sheet.id);
        } else if (
          sheet.type === 'rider-update' ||
          sheet.type === 'edit-rider'
        ) {
          buttonsToRender = getRiderUpdateButtons(sheet.id);
        } else {
          const dynamicButtons = getSheetButtons(sheet.type);
          buttonsToRender =
            dynamicButtons.length > 0 ? dynamicButtons : sheet.buttons;
        }

        return (
          <Sheet
            key={sheet.id}
            open={sheet.isOpen}
            onOpenChange={open => !open && hideSheet(sheet.id)}
          >
            <SheetContent
              side={sheet.side || 'right'}
              className={`
                ${sheet.size === 'sm' ? 'sm:max-w-sm' : ''}
                ${sheet.size === 'md' ? 'sm:max-w-md' : ''}
                ${sheet.size === 'lg' ? 'sm:max-w-lg' : ''}
                ${sheet.size === 'xl' ? 'sm:max-w-xl' : ''}
                ${sheet.size === '2xl' ? 'sm:max-w-2xl' : ''}
                ${sheet.size === '3xl' ? 'sm:max-w-3xl' : ''}
                ${sheet.size === '4xl' ? 'sm:max-w-[800px]' : ''}
                ${sheet.size === 'full' ? 'sm:max-w-full' : ''}
              `}
              data-sheet-type={sheet.type}
              data-sheet-id={sheet.id}
            >
              <SheetHeader>
                {sheet.title && <SheetTitle>{sheet.title}</SheetTitle>}
                <SheetDescription>
                  {/* Hidden description for accessibility */}
                  <span className='sr-only'>
                    Dialog content for {sheet.title || sheet.type}
                  </span>
                </SheetDescription>
              </SheetHeader>

              <div className='flex-1 py-4'>
                {/* Prioritize direct content over registry when content is explicitly provided */}
                {sheet.content ||
                  (sheet.type && renderSheetContent(sheet.type, sheet.data))}
              </div>

              <SheetFooter className=''>
                {/* Check if buttons are explicitly disabled */}
                {sheet.buttons &&
                sheet.buttons.length === 0 ? null : buttonsToRender && // Explicitly no buttons - render empty footer
                  buttonsToRender.length > 0 ? (
                  // Use dynamic buttons if available
                  <>
                    {buttonsToRender.map(button => {
                      const isLoading = button.loading || false;
                      const isDisabled = button.disabled || isLoading;

                      return (
                        <Button
                          key={button.id}
                          color={button.color || 'brand'}
                          hierarchy={button.hierarchy || 'primary'}
                          type={button.type || 'button'}
                          onClick={button.onClick}
                          disabled={isDisabled}
                        >
                          {isLoading ? (
                            <div className='flex items-center gap-2'>
                              <CircleLoader
                                color='currentColor'
                                loading={isLoading}
                                size={16}
                              />
                              {button.label}
                            </div>
                          ) : (
                            <div className='flex items-center gap-2'>
                              {button.icon && button.icon}
                              {button.label}
                            </div>
                          )}
                        </Button>
                      );
                    })}
                  </>
                ) : (
                  // Legacy support - fallback to old button system
                  <>
                    {sheet.onConfirm ? (
                      <Button
                        color='brand'
                        hierarchy='primary'
                        onClick={() => {
                          sheet.onConfirm?.();
                          hideSheet(sheet.id);
                        }}
                      >
                        {sheet.confirmIcon}
                        {sheet.confirmText || 'Confirm'}
                      </Button>
                    ) : (
                      <Button type='submit'>Save changes</Button>
                    )}
                    {sheet.onCancel ? (
                      <Button
                        color='gray'
                        hierarchy='outlined'
                        onClick={() => {
                          sheet.onCancel?.();
                          hideSheet(sheet.id);
                        }}
                      >
                        {sheet.cancelIcon}
                        {sheet.cancelText || 'Cancel'}
                      </Button>
                    ) : (
                      <SheetClose asChild>
                        <Button color='gray' hierarchy='outlined'>
                          Close
                        </Button>
                      </SheetClose>
                    )}
                  </>
                )}
              </SheetFooter>
            </SheetContent>
          </Sheet>
        );
      })}
    </>
  );
};

export default GlobalSheetRenderer;
